import { uniqBy } from 'lodash-es'
import { Lightbulb, Pin } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatCurrency } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import { CoverageRow } from './CoverageRow'
import { InfoTag } from './InfoTag'
import { CarrierProgram } from './QuoteCard'
import {
  CARD_HEADER_MIN_HEIGHT,
  CARRIER_ESTIMATE_EXPLANATION,
  type CoverageQuote,
  isPricedAsOneTotal,
  type OneTimeFee,
  PricingKind,
} from './quoteCardTypes'
import { coverageCartKey } from './quoteCartItems'
import { CartItemScope, QuotesEvent, useTrackQuotesEvent } from './quotesAnalytics'

/** One coverage in a package, with the carrier that priced it. */
export interface PackageLine {
  /** The quote card this coverage came from — what its cart key falls back to. */
  quoteId: string
  carrierName: string
  /** The paper behind the offer, where one brand writes on several. */
  carrierProgram?: string | null
  sourcedThrough?: string | null
  iconUrl: string
  iconAlt: string
  /** Per line, so one package can hold carrier estimates and firm quotes side by side. */
  pricingKind: PricingKind
  coverage: CoverageQuote
}

const RECOMMENDATION_EXPLANATION =
  "This combination of coverages from your quotes and estimates best matches your fleet's needs."

/** Coverages from one or more carriers, put together to be added to the cart as one. Always recommended. */
export interface QuotePackage {
  id: string
  lines: PackageLine[]
  oneTimeFees?: OneTimeFee[]
  /** What the cart charges for these lines, one-sum carriers and fees included. */
  total: number
}

interface PackageCardProps {
  quotePackage: QuotePackage
  onAddCoverages?: (coverageKeys: string[]) => void
  onRemoveCoverages?: (coverageKeys: string[]) => void
  cartKeys?: ReadonlySet<string>
}

/** The same keys each line's own quote card adds, so a package and its quotes share one cart. */
export function packageCartKeys(quotePackage: Pick<QuotePackage, 'lines'>): string[] {
  return quotePackage.lines.map((line) => coverageCartKey(line.quoteId, line.coverage))
}

/** One line per carrier, for the row of logos. */
export function packageCarriers(quotePackage: QuotePackage): PackageLine[] {
  return uniqBy(quotePackage.lines, (line) => line.carrierName)
}

/** Same carrier paper, wholesaler and kind of price — what gets one carrier label over its rows. */
function carrierKey(line: PackageLine): string {
  return `${line.carrierName}|${line.carrierProgram ?? ''}|${line.sourcedThrough ?? ''}|${line.pricingKind}`
}

function groupByCarrier(lines: PackageLine[]): [PackageLine, ...PackageLine[]][] {
  const groups = new Map<string, [PackageLine, ...PackageLine[]]>()
  for (const line of lines) {
    const group = groups.get(carrierKey(line))
    if (group) group.push(line)
    else groups.set(carrierKey(line), [line])
  }
  return [...groups.values()]
}

/** The package's lines split by the offer each came from, keyed by that offer's indication. */
function keysByOffer(lines: PackageLine[]): Map<string, { line: PackageLine; keys: string[] }> {
  const offers = new Map<string, { line: PackageLine; keys: string[] }>()
  for (const line of lines) {
    const indicationId = line.coverage.indicationId ?? line.quoteId
    const offer = offers.get(indicationId) ?? { line, keys: [] }
    offer.keys.push(coverageCartKey(line.quoteId, line.coverage))
    offers.set(indicationId, offer)
  }
  return offers
}

export function PackageCard({ quotePackage, onAddCoverages, onRemoveCoverages, cartKeys }: PackageCardProps) {
  const track = useTrackQuotesEvent()
  const { lines, oneTimeFees, total } = quotePackage
  const keys = packageCartKeys(quotePackage)
  const inCart = keys.length > 0 && keys.every((key) => cartKeys?.has(key))
  // As on its own card: an offer priced as one total shows no price on its lines, not "Included".
  const pricedAsOneTotal = (line: PackageLine) =>
    isPricedAsOneTotal(lines.filter((other) => other.quoteId === line.quoteId).map((other) => other.coverage))

  // One event per offer, as its own card would send: each names the carrier its engagement alert reports.
  function trackEachOffer(event: QuotesEvent.CART_ITEM_ADDED | QuotesEvent.CART_ITEM_REMOVED) {
    keysByOffer(lines).forEach(({ line, keys: offerKeys }, indicationId) =>
      track(event, {
        scope: CartItemScope.PACKAGE,
        item_count: offerKeys.length,
        package_id: quotePackage.id,
        indication_id: indicationId,
        carrier_name: line.carrierName,
        carrier_program: line.carrierProgram ?? null,
      })
    )
  }

  function add() {
    trackEachOffer(QuotesEvent.CART_ITEM_ADDED)
    onAddCoverages?.(keys)
  }

  function remove() {
    trackEachOffer(QuotesEvent.CART_ITEM_REMOVED)
    onRemoveCoverages?.(keys)
  }

  return (
    <div className="bg-card flex w-full flex-col overflow-hidden rounded-xl border">
      <div className="flex flex-col gap-4 p-4">
        {/* Wraps, so a many-carrier package drops the badge to its own line rather than clipping it. */}
        <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', CARD_HEADER_MIN_HEIGHT)}>
          <div className="flex shrink-0 gap-2">
            {packageCarriers(quotePackage).map((line) => (
              <img
                key={line.carrierName}
                src={line.iconUrl}
                alt={line.iconAlt}
                className="border-ink-100 bg-card size-11 rounded-full border object-contain"
              />
            ))}
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {/* A popover, not a tooltip: it has to open on a tap. */}
            <Popover
              onOpenChange={(open) =>
                open && track(QuotesEvent.CARD_INFO_OPENED, { info_label: 'Recommended package' })
              }
            >
              <PopoverTrigger className="rounded-md">
                <Badge variant="outline" className="border-brand-300 bg-brand-200 text-brand-900">
                  Recommended package
                </Badge>
              </PopoverTrigger>
              <PopoverContent className="w-64 text-sm">{RECOMMENDATION_EXPLANATION}</PopoverContent>
            </Popover>
            {/* Pinned, not removable: staff put the package here, so the customer has no delete. */}
            <Pin role="img" aria-label="Pinned package" className="text-ink-300 size-4" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-foreground text-3xl font-extrabold">{formatCurrency(total)}</span>
          <span className="text-ink-500 text-sm"> / year</span>
        </div>

        <div className="flex flex-col gap-4">
          {groupByCarrier(lines).map(([carrier, ...rest]) => (
            <div key={carrierKey(carrier)} className="flex flex-col gap-2">
              <div className="flex items-center gap-2 px-2">
                <img
                  src={carrier.iconUrl}
                  alt=""
                  className="border-ink-100 size-6 shrink-0 rounded-full border object-contain"
                />
                <div className="flex min-w-0 flex-col">
                  <span className="text-muted-foreground text-xs font-semibold">{carrier.carrierName}</span>
                  <CarrierProgram program={carrier.carrierProgram} />
                </div>
                {carrier.pricingKind === PricingKind.INDICATION && (
                  <div className="ml-auto">
                    <InfoTag label="Carrier estimate" explanation={CARRIER_ESTIMATE_EXPLANATION} />
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-4">
                {[carrier, ...rest].map((line) => (
                  <CoverageRow
                    key={coverageCartKey(line.quoteId, line.coverage)}
                    coverage={line.coverage}
                    pricedAsOneTotal={pricedAsOneTotal(line)}
                  />
                ))}
              </div>
              {carrier.sourcedThrough && carrier.sourcedThrough !== carrier.carrierName && (
                <span className="text-ink-400 px-2 text-xs">Sourced through {carrier.sourcedThrough}</span>
              )}
            </div>
          ))}
          {!!oneTimeFees?.length && (
            <div className="flex flex-col gap-1 px-2">
              <span className="text-ink-300 text-xs font-bold">One-time fees</span>
              {oneTimeFees.map((fee) => (
                <div key={fee.id} className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground text-sm">{fee.label}</span>
                  <span className="text-foreground text-sm font-semibold">{formatCurrency(fee.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-start gap-2 rounded-lg p-3">
          <Lightbulb className="text-ink-500 mt-0.5 size-4 shrink-0" aria-hidden />
          <p className="text-foreground text-xs">
            With Cai, managing multiple insurance providers is as easy as working with one.
          </p>
        </div>

        {inCart ? (
          <div className="flex flex-col gap-1">
            <Button className="w-full" disabled>
              Added to cart
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={remove}
              className="self-center text-red-700 hover:bg-red-50 hover:text-red-800"
            >
              Remove
            </Button>
          </div>
        ) : (
          <Button className="w-full" onClick={add}>
            {`Add package to cart (${lines.length})`}
          </Button>
        )}
      </div>
    </div>
  )
}
